// FILE: apps/admin/src/api.ts
// VERSION: 1.0.0
// START_MODULE_CONTRACT
//   PURPOSE: Typed HTTP client, authentication helpers (magic link + DM 8-digit code), Step-Up OTP helpers, and formatters for Admin Panel.
//   SCOPE: token, ApiError, api (get/post/put/delete), exchangeLogin, startDmLogin, verifyDmLogin, requestStepUp, verifyStepUp, executeAdminAction, rub, gram, num, dt.
//   DEPENDS: none
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   ApiError - HTTP error wrapper with status code
//   api - Typed fetch wrapper for /api/v1 endpoints
//   dt - Format ISO date/time string in ru-RU locale
//   exchangeLogin - Exchange magic-link token for operator session token
//   executeAdminAction - Execute unified 24-kind privileged admin action with reason and optional Step-Up grant
//   gram - Format milli-GRAM integer into human-readable GRAM string
//   num - Format integer/float in ru-RU locale
//   requestStepUp - Initiate Telegram DM 6-digit OTP Step-Up challenge
//   rub - Format kopecks integer into RUB string
//   startDmLogin - Initiate 8-digit Telegram DM login code challenge
//   token - Session token storage helper
//   verifyDmLogin - Verify 8-digit Telegram DM login code and store session token
//   verifyStepUp - Verify 6-digit Step-Up OTP and receive single-use grantToken
// END_MODULE_MAP

const BASE = '/api/v1';
const TOKEN_KEY = 'admin_token';

export const token = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t: string) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

// START_CONTRACT: ApiError
//   PURPOSE: Represent an HTTP error response from the Admin API.
//   INPUTS: status number, message string
//   OUTPUTS: ApiError instance
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: ApiError
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // START_BLOCK_REQUEST
  const t = token.get();
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(t ? { Authorization: `Bearer ${t}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (res.status === 401) {
    token.clear();
    if (!location.hash.startsWith('#login=')) location.assign('/');
    throw new ApiError(401, 'Сессия истекла');
  }
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { message?: string | string[] };
      if (Array.isArray(body.message)) message = body.message.join(', ');
      else if (body.message) message = body.message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
  // END_BLOCK_REQUEST
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

// START_CONTRACT: exchangeLogin
//   PURPOSE: Exchange a magic-link login token from URL hash for a bearer session token.
//   INPUTS: loginToken string
//   OUTPUTS: Promise<boolean>
//   SIDE_EFFECTS: Stores admin_token in localStorage
//   LINKS: M-ADMIN
// END_CONTRACT: exchangeLogin
export async function exchangeLogin(loginToken: string): Promise<boolean> {
  // START_BLOCK_EXCHANGE_LOGIN
  try {
    const res = await fetch(`${BASE}/admin/auth/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: loginToken }),
    });
    if (!res.ok) return false;
    const data = (await res.json()) as { sessionToken: string };
    token.set(data.sessionToken);
    return true;
  } catch {
    return false;
  }
  // END_BLOCK_EXCHANGE_LOGIN
}

// START_CONTRACT: startDmLogin
//   PURPOSE: Request an 8-digit login code via Telegram Bot DM for an operator identifier.
//   INPUTS: identifier (Telegram ID or @username)
//   OUTPUTS: Challenge metadata { challengeId, expiresAt }
//   SIDE_EFFECTS: Triggers bot DM to operator
//   LINKS: M-ADMIN
// END_CONTRACT: startDmLogin
export async function startDmLogin(
  identifier: string,
): Promise<{ challengeId: string; expiresAt: string }> {
  // START_BLOCK_START_DM_LOGIN
  const res = await fetch(`${BASE}/admin/auth/dm-login/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifier }),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new ApiError(res.status, err.message ?? 'Не удалось отправить код входа');
  }
  return (await res.json()) as { challengeId: string; expiresAt: string };
  // END_BLOCK_START_DM_LOGIN
}

// START_CONTRACT: verifyDmLogin
//   PURPOSE: Verify 8-digit Telegram DM login code and persist session token.
//   INPUTS: challengeId, code
//   OUTPUTS: Promise<boolean>
//   SIDE_EFFECTS: Stores admin_token in localStorage
//   LINKS: M-ADMIN
// END_CONTRACT: verifyDmLogin
export async function verifyDmLogin(challengeId: string, code: string): Promise<boolean> {
  // START_BLOCK_VERIFY_DM_LOGIN
  const res = await fetch(`${BASE}/admin/auth/dm-login/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ challengeId, code }),
  });
  if (!res.ok) return false;
  const data = (await res.json()) as { sessionToken: string };
  token.set(data.sessionToken);
  return true;
  // END_BLOCK_VERIFY_DM_LOGIN
}

// START_CONTRACT: requestStepUp
//   PURPOSE: Initiate a 6-digit Telegram DM OTP Step-Up challenge for a high-risk action.
//   INPUTS: action string
//   OUTPUTS: { challengeId, action, expiresAt }
//   SIDE_EFFECTS: Sends OTP DM to operator
//   LINKS: M-ADMIN
// END_CONTRACT: requestStepUp
export function requestStepUp(action: string) {
  // START_BLOCK_REQUEST_STEP_UP
  return api.post<{ challengeId: string; action: string; expiresAt: string }>(
    '/admin/step-up/request',
    { action },
  );
  // END_BLOCK_REQUEST_STEP_UP
}

// START_CONTRACT: verifyStepUp
//   PURPOSE: Verify 6-digit Step-Up OTP code and obtain a single-use grantToken.
//   INPUTS: challengeId, code
//   OUTPUTS: { grantToken, action, expiresAt }
//   SIDE_EFFECTS: Marks OTP verified
//   LINKS: M-ADMIN
// END_CONTRACT: verifyStepUp
export function verifyStepUp(challengeId: string, code: string) {
  // START_BLOCK_VERIFY_STEP_UP
  return api.post<{ grantToken: string; action: string; expiresAt: string }>(
    '/admin/step-up/verify',
    { challengeId, code },
  );
  // END_BLOCK_VERIFY_STEP_UP
}

// START_CONTRACT: executeAdminAction
//   PURPOSE: Execute any of the 24 privileged admin actions with mandatory reason and optional Step-Up token.
//   INPUTS: kind, targetId, payload, reason, stepUpGrantToken
//   OUTPUTS: Execution result
//   SIDE_EFFECTS: Mutates backend state and records audit log
//   LINKS: M-ADMIN
// END_CONTRACT: executeAdminAction
export function executeAdminAction(input: {
  kind: string;
  targetId: string;
  payload?: Record<string, unknown>;
  reason: string;
  stepUpGrantToken?: string;
}) {
  // START_BLOCK_EXECUTE_ADMIN_ACTION
  return api.post<{ ok: boolean; kind: string; targetId: string; result: Record<string, unknown> }>(
    '/admin/actions/execute',
    input,
  );
  // END_BLOCK_EXECUTE_ADMIN_ACTION
}

// ── shared formatters ──
// START_CONTRACT: rub
//   PURPOSE: Format integer kopecks into localized RUB string.
//   INPUTS: kopecks number
//   OUTPUTS: Formatted string
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: rub
export const rub = (kopecks: number) =>
  (kopecks / 100).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// START_CONTRACT: gram
//   PURPOSE: Format integer milli-GRAM into localized GRAM string.
//   INPUTS: milliGram number
//   OUTPUTS: Formatted string
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: gram
export const gram = (milliGram: number) =>
  (milliGram / 1000).toLocaleString('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// START_CONTRACT: num
//   PURPOSE: Format number in ru-RU locale.
//   INPUTS: n number
//   OUTPUTS: Formatted string
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: num
export const num = (n: number) => (n ?? 0).toLocaleString('ru-RU');

// START_CONTRACT: dt
//   PURPOSE: Format ISO date string in ru-RU short date+time format.
//   INPUTS: iso string | null | undefined
//   OUTPUTS: Formatted string
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: dt
export const dt = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' }) : '—';
